import { useLayoutEffect } from "react";
import { BrowserRouter, Routes, Route, useLocation, Navigate } from "react-router";
import { useLenis } from "lenis/react";
import { Navigation } from "./components/Navigation";
import { Footer } from "./components/Footer";
import { SmoothScroll } from "./components/SmoothScroll";
import { HomePage } from "./pages/HomePage";
import { ProductsPage } from "./pages/ProductsPage";
import { ProductSlugPage } from "./pages/products/ProductSlugPage";
import { MaterialsLibraryPage } from "./pages/materials/MaterialsLibraryPage";
import { PackagingOptionsPage } from "./pages/PackagingOptionsPage";
import { ServicesPage } from "./pages/ServicesPage";
import { IndustriesPage } from "./pages/IndustriesPage";
import { SustainabilityPage } from "./pages/SustainabilityPage";
import { ProcessPage } from "./pages/ProcessPage";
import { QuotePage } from "./pages/QuotePage";
import { AboutPage } from "./pages/AboutPage";
import { ContactPage } from "./pages/ContactPage";
import { BlogPage } from "./pages/BlogPage";
import { BlogDetailPage } from "./pages/BlogDetailPage";
import { PrivacyPolicyPage } from "./pages/PrivacyPolicyPage";
import { TermsPage } from "./pages/TermsPage";
import { CookiePolicyPage } from "./pages/CookiePolicyPage";
import { LegalAndCertificationsPage } from "./pages/LegalAndCertificationsPage";
import { GalleryPage } from "./pages/GalleryPage";
import { FacilityPage } from "./pages/FacilityPage";
import { SchemaMarkup } from "./components/SchemaMarkup";
import { Breadcrumb } from "./components/Breadcrumb";
import { PageMeta } from "./components/PageMeta";
import { BusinessInfoWidget } from "./components/BusinessInfoWidget";

function ScrollToTop() {
  const { pathname } = useLocation();
  const lenis = useLenis();

  useLayoutEffect(() => {
    if ("scrollRestoration" in history) history.scrollRestoration = "manual";
  }, []);

  useLayoutEffect(() => {
    if (lenis) {
      lenis.scrollTo(0, { immediate: true });
      return;
    }
    document.documentElement.scrollTop = 0;
    document.body.scrollTop = 0;
    window.scrollTo(0, 0);
    // Fallback: some mobile browsers defer scroll until after layout
    const t = setTimeout(() => {
      document.documentElement.scrollTop = 0;
      document.body.scrollTop = 0;
      window.scrollTo(0, 0);
    }, 0);
    return () => clearTimeout(t);
  }, [pathname]); // eslint-disable-line react-hooks/exhaustive-deps

  return null;
}

export default function App() {
  return (
    <BrowserRouter>
      <SmoothScroll>
        <ScrollToTop />
        <SchemaMarkup />
        <PageMeta />
        <div className="flex min-h-screen min-w-0 flex-col overflow-x-hidden">
        <Navigation />
        <main className="min-w-0 flex-1 overflow-x-hidden pt-16">
          <Breadcrumb />
          <Routes>
            <Route path="/" element={<HomePage />} />
            <Route path="/products" element={<ProductsPage />} />
            <Route path="/products/rigid-boxes" element={<Navigate to="/products" replace />} />
            <Route path="/products/:slug" element={<ProductSlugPage />} />

            <Route path="/options" element={<PackagingOptionsPage />} />

            <Route path="/materials" element={<MaterialsLibraryPage />} />
            <Route path="/materials/sbs-c1s" element={<MaterialsLibraryPage />} />
            <Route path="/materials/sbs-c2s" element={<MaterialsLibraryPage />} />
            <Route path="/materials/kraft" element={<MaterialsLibraryPage />} />
            <Route path="/materials/duplex" element={<MaterialsLibraryPage />} />
            <Route path="/materials/corrugated-kraft" element={<MaterialsLibraryPage />} />
            <Route path="/materials/corrugated-white" element={<MaterialsLibraryPage />} />
            <Route path="/materials/flute-types" element={<MaterialsLibraryPage />} />
            <Route path="/materials/chipboard" element={<MaterialsLibraryPage />} />
            <Route path="/materials/wrapped-materials" element={<MaterialsLibraryPage />} />

            <Route path="/finishes" element={<MaterialsLibraryPage />} />
            <Route path="/finishes/matte" element={<MaterialsLibraryPage />} />
            <Route path="/finishes/gloss" element={<MaterialsLibraryPage />} />
            <Route path="/finishes/soft-touch" element={<MaterialsLibraryPage />} />
            <Route path="/finishes/anti-scratch" element={<MaterialsLibraryPage />} />
            <Route path="/finishes/foil-stamping" element={<MaterialsLibraryPage />} />
            <Route path="/finishes/emboss-deboss" element={<MaterialsLibraryPage />} />
            <Route path="/finishes/spot-uv" element={<MaterialsLibraryPage />} />
            <Route path="/finishes/offset-printing" element={<MaterialsLibraryPage />} />
            <Route path="/finishes/digital-printing" element={<MaterialsLibraryPage />} />
            <Route path="/finishes/uv-printing" element={<MaterialsLibraryPage />} />

            <Route path="/services" element={<ServicesPage />} />
            <Route path="/industries" element={<IndustriesPage />} />
            <Route path="/sustainability" element={<SustainabilityPage />} />
            <Route path="/process" element={<ProcessPage />} />
            <Route path="/quote" element={<QuotePage />} />
            <Route path="/about" element={<AboutPage />} />
            <Route path="/gallery" element={<GalleryPage />} />
            <Route path="/facility" element={<FacilityPage />} />
            <Route path="/contact" element={<ContactPage />} />
            <Route path="/blog" element={<BlogPage />} />
            <Route path="/blog/:slug" element={<BlogDetailPage />} />
            <Route path="/privacy-policy" element={<PrivacyPolicyPage />} />
            <Route path="/terms" element={<TermsPage />} />
            <Route path="/cookie-policy" element={<CookiePolicyPage />} />
            <Route path="/legal-and-certifications" element={<LegalAndCertificationsPage />} />
          </Routes>
        </main>
        <Footer />
        <BusinessInfoWidget />
        </div>
      </SmoothScroll>
    </BrowserRouter>
  );
}